import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Loader2, X } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-click "Publish to Community" button used from CGI / Timeline / Composer
 * completion banners. Pops a tiny modal to capture caption + tags before
 * shipping the render to the shared community feed.
 */
const PublishToCommunityButton = ({
  sourceCollection,    // "cgi_render_tasks" | "timeline_projects" | "composer_projects" | "video_tasks" | "content"
  sourceId,
  defaultCaption = "",
  alreadyPublished = false,
  testIdPrefix = "publish",
  className = "",
}) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState(defaultCaption);
  const [tagsRaw, setTagsRaw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [published, setPublished] = useState(alreadyPublished);

  const auth = { Authorization: `Bearer ${token}` };

  const submit = async () => {
    setSubmitting(true);
    try {
      const tags = tagsRaw
        .split(/[\s,]+/)
        .map(t => t.replace(/^#/, "").trim().toLowerCase())
        .filter(Boolean)
        .slice(0, 8);
      const payload = {
        source_collection: sourceCollection,
        source_id: sourceId,
        caption: caption.trim() || null,
        tags: tags.length ? tags : null,
      };
      const r = await axios.post(`${API}/community/share`, payload, { headers: auth });
      setPublished(true);
      setOpen(false);
      if (r.data.already_shared) {
        toast.success("Already in community 🎉");
      } else {
        toast.success("Posted to community 🎉");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish.");
    } finally {
      setSubmitting(false);
    }
  };

  if (published) {
    return (
      <span
        data-testid={`${testIdPrefix}-published`}
        className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-600 ${className}`}
      >
        <Share2 className="w-3 h-3" /> Published to community
      </span>
    );
  }

  return (
    <>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testIdPrefix}-button`}
        className={`bg-coral-500 hover:bg-coral-600 text-white ${className}`}
      >
        <Share2 className="w-4 h-4 mr-1" /> Publish to community
      </Button>

      {open && (
        <div
          data-testid={`${testIdPrefix}-modal`}
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-white rounded-3xl w-full max-w-md p-5 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
                Publish to Community
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid={`${testIdPrefix}-modal-close`}
                className="text-ink-400 hover:text-ink-950"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, 500))}
              placeholder="What's the story behind this render? (optional, 500 chars)"
              data-testid={`${testIdPrefix}-caption`}
              rows={4}
              className="w-full p-3 rounded-2xl border border-ink-200 focus:outline-none focus:border-blue-500 text-sm"
            />
            <input
              type="text"
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value.slice(0, 200))}
              placeholder="Tags (comma or space separated, max 8)"
              data-testid={`${testIdPrefix}-tags`}
              className="w-full p-3 rounded-2xl border border-ink-200 focus:outline-none focus:border-blue-500 text-sm"
            />
            <p className="text-[10px] text-ink-500">
              Posts feed into the shared XEA community across Marketing, Designer and CGI modes.
              Top-20 leaderboard runs every weekend.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setOpen(false)}
                      data-testid={`${testIdPrefix}-cancel`}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={submitting}
                      data-testid={`${testIdPrefix}-submit`}
                      className="bg-coral-500 hover:bg-coral-600 text-white">
                {submitting
                  ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  : <Share2 className="w-4 h-4 mr-1" />}
                Publish
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


export default PublishToCommunityButton;
